import React, { useEffect, useState, useMemo } from 'react'
import { Box, Alert, CircularProgress, Tabs, Tab } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { apiService } from '../services/api'
import SendReportModal from '../components/dashboard/SendReportModal'
import {
  ReportPage, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, Icon, fmt, theme,
  useDistinctFilters, buildOplFilterFields,
} from '../components/report-shell'

const yesterdayIso = () => {
  const d = new Date(); d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const nextDayIso = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(Date.UTC(year, month - 1, day + 1))
  return d.toISOString().slice(0, 10)
}

// Polish plural for "region": 1 region / 2-4 regiony / 5+ regionów
// (teens take the -ów form, hence the 12-14 carve-out).
const plRegions = (n) => {
  if (n === 1) return 'region'
  const ones = n % 10, teens = n % 100
  return ones >= 2 && ones <= 4 && !(teens >= 12 && teens <= 14) ? 'regiony' : 'regionów'
}

const OrangeDailySalesPage = () => {
  const [day, setDay] = useState(yesterdayIso())
  const [filters, setFilters] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)
  const [sendModal, setSendModal] = useState({ open: false, scope: null, value: null })

  const distinct = useDistinctFilters('opl')
  const allFilters = () => ({ day, ...filters })

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const resp = await apiService.getOrangeRegionDaily(allFilters())
      setData(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true)
    try {
      const resp = await apiService.exportOrangeRegionDaily(allFilters())
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Sprzedaz_dzienna_OPL_${day}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [day, JSON.stringify(filters)])

  const T = data?.totals || {}

  // ─ Roll-up columns (region/dystrykt/agent/KAM) ─────────────
  const rollupCols = (labelField, label, scope) => [
    {
      field: '_send', header: '', width: '40px', sortable: false,
      render: (_v, row) => {
        const value = row[scope] || row.region || row.dystrykt || row.agent || row.kam
        if (!value || value === '(brak)') return null
        return (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); setSendModal({ open: true, scope, value }) }}
            title={`Wyślij raport (${scope})`}
            style={{
              display: 'inline-flex', cursor: 'pointer',
              color: theme.textSub, padding: 4, borderRadius: 5,
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.accent}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.textSub}
          >
            <EmailIcon fontSize="small" sx={{ width: 14, height: 14 }} />
          </span>
        )
      },
    },
    { field: labelField, header: label, width: '1.5fr', minWidth: 180 },
    // Districts/agents carry their containing region(s); regions/KAMs don't
    // need a parent column. Multi-region agents list every region (full value
    // in the title attribute so nothing is hidden by truncation) — the
    // per-region split lives in the "Agent x region" tab.
    ...(labelField === 'dystrykt' || labelField === 'agent'
      ? [{
          field: 'region', header: 'Region', width: '170px',
          render: (v, row) => (
            <span title={v || ''} style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {row.region_count > 1
                ? `${row.region_count} ${plRegions(row.region_count)}: ${v}`
                : (v || '—')}
            </span>
          ),
        }]
      : []),
    { field: 'salon_count',  header: 'Salonów',     width: '95px',  numeric: true },
    { field: 'opos_day',     header: 'OPOS (dzień)', width: '120px', sales: true },
    { field: 'loc_day',      header: 'Loc (dzień)',  width: '110px', sales: true },
    { field: 'total_day',    header: 'TOTAL (dzień)', width: '120px', sales: true },
    { field: 'opos_mtd',     header: 'OPOS (MTD)',   width: '110px', numeric: true },
    { field: 'loc_mtd',      header: 'Loc (MTD)',    width: '105px', numeric: true },
    { field: 'total_mtd',    header: 'TOTAL (MTD)',  width: '115px', numeric: true },
    { field: 'avg_opos_per_pos', header: 'Śr. OPOS / POS', width: '130px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'avg_loc_per_pos', header: 'Śr. Loc / POS', width: '125px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'pct_opos_mtd', header: 'Udział % OPOS', width: '125px',
      numeric: true, format: (v) => fmt.pct((Number(v) || 0) * 100) },
    { field: 'pct_loc_mtd', header: 'Udział % Loc', width: '120px',
      numeric: true, format: (v) => fmt.pct((Number(v) || 0) * 100) },
  ]

  // Agent×region: region first (so it reads as a grouping), then the same
  // metric columns as the other rollups.
  const agentRegionCols = useMemo(() => ([
    { field: 'region', header: 'Region', width: '150px' },
    { field: 'agent', header: 'Agent', width: '1.5fr', minWidth: 180 },
    { field: 'salon_count', header: 'Salonów', width: '95px', numeric: true },
    { field: 'opos_day', header: 'OPOS (dzień)', width: '120px', sales: true },
    { field: 'loc_day', header: 'Loc (dzień)', width: '110px', sales: true },
    { field: 'total_day', header: 'TOTAL (dzień)', width: '120px', sales: true },
    { field: 'opos_mtd', header: 'OPOS (MTD)', width: '110px', numeric: true },
    { field: 'loc_mtd', header: 'Loc (MTD)', width: '105px', numeric: true },
    { field: 'total_mtd', header: 'TOTAL (MTD)', width: '115px', numeric: true },
    { field: 'avg_opos_per_pos', header: 'Śr. OPOS / POS', width: '130px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'avg_loc_per_pos', header: 'Śr. Loc / POS', width: '125px',
      numeric: true, format: (v) => (Number(v) || 0).toFixed(2) },
    { field: 'pct_opos_mtd', header: 'Udział % OPOS', width: '125px',
      numeric: true, format: (v) => fmt.pct((Number(v) || 0) * 100) },
    { field: 'pct_loc_mtd', header: 'Udział % Loc', width: '120px',
      numeric: true, format: (v) => fmt.pct((Number(v) || 0) * 100) },
  ]), [])

  const salonCols = useMemo(() => ([
    { field: 'kod_bscs', header: 'BSCS',  width: '110px' },
    { field: 'city',     header: 'Miasto', width: '140px' },
    { field: 'kam',      header: 'KAM',    width: '110px' },
    { field: 'agent',    header: 'Agent',  width: '1.4fr', minWidth: 180 },
    { field: 'rksa',     header: 'RKSA',   width: '160px' },
    { field: 'xdm',      header: 'XDM',    width: '150px' },
    { field: 'siec_sprzedazy', header: 'Sieć', width: '100px' },
    { field: 'opos_day',  header: 'OPOS (dz)', width: '105px', sales: true },
    { field: 'loc_day',   header: 'Loc (dz)',  width: '95px',  sales: true },
    { field: 'total_day', header: 'TOTAL (dz)', width: '105px', sales: true },
    { field: 'opos_mtd',  header: 'OPOS (MTD)', width: '110px', numeric: true },
    { field: 'loc_mtd',   header: 'Loc (MTD)',  width: '100px', numeric: true },
    { field: 'total_mtd', header: 'TOTAL (MTD)', width: '115px', numeric: true },
  ]), [])

  const filterBar = (
    <FilterBar
      periods={[
        {
          type: 'date', key: 'day', label: 'Dzień raportu',
          value: day, onChange: (v) => setDay(v || yesterdayIso()),
        },
      ]}
      periodRight={data?.month_start ? `MTD od ${data.month_start}` : null}
      filters={buildOplFilterFields(distinct, filters, setFilters)}
      onReset={() => setFilters({})}
    />
  )

  const kpiTiles = data ? [
    { key: 'salons',  label: 'Salony w zakresie', value: fmt.int(T.salon_count || 0) },
    { key: 'sold',    label: 'Sprzedały dziś',
      value: `${T.salons_with_sale_today || 0} / ${T.salon_count || 0}`, accent: '#16a34a' },
    { key: 'today',   label: `Sprzedaż dnia (${day})`,
      value: fmt.int(T.total_day || 0), unit: 'szt.', accent: '#ff7900',
      sub: `OPOS ${fmt.int(T.opos_day || 0)} · Loc ${fmt.int(T.loc_day || 0)}` },
    { key: 'mtd',     label: 'Sprzedaż MTD',
      value: fmt.int(T.total_mtd || 0), unit: 'szt.', accent: '#2563eb',
      sub: `OPOS ${fmt.int(T.opos_mtd || 0)} · Loc ${fmt.int(T.loc_mtd || 0)}` },
  ] : []

  const tabConfigs = [
    { label: `Per region (${data?.regions?.length || 0})`,    rows: data?.regions   || [], cols: rollupCols('region',   'Region',   'region'),   keyField: 'region' },
    { label: `Per dystrykt (${data?.districts?.length || 0})`, rows: data?.districts || [], cols: rollupCols('dystrykt', 'Dystrykt', 'district'), keyField: 'dystrykt' },
    { label: `Per agent (${data?.agents?.length || 0})`,       rows: data?.agents    || [], cols: rollupCols('agent',    'Agent',    'agent'),    keyField: 'agent' },
    // Reconciliation view: one row per agent×region, so a multi-region agent's
    // numbers can be checked against both the region and the agent tab.
    { label: `Agent × region (${data?.agents_by_region?.length || 0})`,
      rows: data?.agents_by_region || [], cols: agentRegionCols,
      rowId: (r) => `${r.region}|${r.agent}` },
    { label: `Per KAM (${data?.kams?.length || 0})`,           rows: data?.kams      || [], cols: rollupCols('kam',      'KAM',      'kam'),      keyField: 'kam' },
    { label: `Salony (${data?.salons?.length || 0})`,          rows: data?.salons    || [], cols: salonCols,                                                keyField: 'plant_code', isSalons: true },
  ]
  const active = tabConfigs[tab]

  return (
    <ReportPage
      actions={
        <>
          <HeaderButton onClick={fetchData} disabled={loading}>
            <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
          </HeaderButton>
          <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
            <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
          </HeaderButton>
        </>
      }
      filterBar={filterBar}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && !loading && (
        <Section
          title="Roll-up sprzedaży"
          right={
            <Box sx={{ borderBottom: 0 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
                {tabConfigs.map((t, i) => <Tab key={i} label={t.label} />)}
              </Tabs>
            </Box>
          }
        >
          <ReportTable
            rows={active.rows.map((r, i) => ({
              id: (active.rowId ? active.rowId(r) : r[active.keyField]) || i,
              ...r,
            }))}
            columns={active.cols}
            initialSort={active.isSalons
              ? { field: 'total_day', dir: 'desc' }
              : { field: 'total_mtd', dir: 'desc' }}
            pageSize={active.isSalons ? 50 : 25}
            pageSizeOptions={[25, 50, 100, 500]}
            density="compact"
          />
        </Section>
      )}

      <SendReportModal
        open={sendModal.open}
        onClose={() => setSendModal({ open: false, scope: null, value: null })}
        operator="orange"
        scope={sendModal.scope}
        scopeValue={sendModal.value}
        reportType="orange_daily_sales"
        periodStart={day}
        periodEnd={nextDayIso(day)}
      />
    </ReportPage>
  )
}

export default OrangeDailySalesPage
